import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { setSaveAuthHandler } from "@/lib/api";
import { AccountLogin } from "@/components/AccountLogin";
import { toast } from "sonner";

const AuthContext = createContext(null);
const names = { bucket: "bucket list item", itinerary: "itinerary", saves: "place", packing: "packing list" };
const errorText = (error) => typeof error.response?.data?.detail === "string" ? error.response.data.detail : "Could not finish saving. Your entry is safe; please try again.";
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [accountEpoch, setAccountEpoch] = useState(0);
  const [open, setOpen] = useState(false);
  const [loginAccount, setLoginAccount] = useState(null);
  const [intent, setIntent] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [resumeError, setResumeError] = useState("");
  const [duplicateResume, setDuplicateResume] = useState(false);
  const waiter = useRef(null);
  const currentUser = useRef(null);
  const callbackMode = useRef(false);
  const section = useRef(null);
  const finishing = useRef(false);
  const acceptUser = useCallback((value) => { currentUser.current = value; setUser(value); }, []);
  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      acceptUser(data);
      if (data.needs_name) { setLoginAccount(data); setOpen(true); }
    }
    catch { acceptUser(null); }
    finally { setLoading(false); }
  }, [acceptUser]);

  const finishAuthentication = useCallback(async (account, allowDuplicate = false) => {
    setLoginAccount(account);
    if (account.needs_name) { setOpen(true); return; }
    if (finishing.current) return;
    finishing.current = true;
    acceptUser(account); setCompleting(true); setResumeError("");
    try {
      const { data: pending } = await api.get("/auth/pending");
      let completed;
      if (pending.pending) {
        section.current = pending.section;
        const response = await api.post("/auth/pending/commit", null, { params: { allow_duplicate: allowDuplicate } });
        completed = response.data;
        await api.delete("/auth/pending");
      }
      const waiting = waiter.current;
      waiter.current = null;
      setOpen(false); setIntent(null); setLoginAccount(null);
      if (waiting && completed) waiting.resolve({ data: completed.result, status: 200, statusText: "OK", headers: {}, config: waiting.config });
      else if (waiting) waiting.reject(new Error("Your unfinished save expired. Please try saving again."));
      setRefreshKey((value) => value + 1);
      if (callbackMode.current) {
        callbackMode.current = false;
        navigate("/", { replace: true, state: { section: completed?.section || section.current, resumedSave: completed?.result?.id } });
      }
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.code === "duplicate_save" && waiter.current) {
        waiter.current.reject(error); waiter.current = null;
        setOpen(false); setIntent(null); setRefreshKey((value) => value + 1);
        await api.delete("/auth/pending").catch(() => {});
      } else {
        const duplicate = error.response?.status === 409 && error.response?.data?.detail?.code === "duplicate_save";
        setDuplicateResume(duplicate);
        setResumeError(duplicate ? "A similar place is already in My Saves. Would you like to save another copy?" : errorText(error)); setOpen(true);
      }
    } finally { finishing.current = false; setCompleting(false); }
  }, [acceptUser, navigate]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    setSaveAuthHandler(async (config, path) => {
      if (waiter.current) throw new Error("Please finish signing in before starting another save.");
      const payload = typeof config.data === "string" ? JSON.parse(config.data) : config.data;
      const query = new URL(config.url, window.location.origin).searchParams;
      const { data } = await api.post("/auth/pending", { method: config.method.toUpperCase(), path, payload, allow_duplicate: config.params?.allow_duplicate === true || query.get("allow_duplicate") === "true" });
      if (currentUser.current) { acceptUser(null); setAccountEpoch((value) => value + 1); }
      section.current = data.section;
      setIntent(names[data.section]); setResumeError(""); setDuplicateResume(false); setLoginAccount(null); setOpen(true);
      return new Promise((resolve, reject) => { waiter.current = { resolve, reject, config }; });
    });
    return () => setSaveAuthHandler(null);
  }, [acceptUser]);

  const cancelLogin = async () => {
    if (completing) return;
    if (loginAccount?.needs_name) { await api.post("/auth/logout").catch(() => {}); acceptUser(null); }
    await api.delete("/auth/pending").catch(() => {});
    const waiting = waiter.current;
    waiter.current = null;
    if (waiting) waiting.reject({ response: { data: { detail: "Sign-in cancelled. Your entry has not been saved." } } });
    setOpen(false); setIntent(null); setLoginAccount(null); setResumeError("");
    if (callbackMode.current) { callbackMode.current = false; navigate("/", { replace: true, state: { section: section.current } }); }
  };

  const openLogin = () => { setIntent(null); setResumeError(""); setLoginAccount(user?.needs_name ? user : null); setOpen(true); };
  const googleLogin = () => {
    const redirect = new URL("/", window.location.origin);
    if (section.current) redirect.searchParams.set("resume", section.current);
    window.location.assign(`https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect.toString())}`);
  };
  const completeOAuth = useCallback(async (sessionId) => {
    callbackMode.current = true;
    section.current = new URLSearchParams(window.location.search).get("resume");
    setIntent(names[section.current] || null); setOpen(true); setCompleting(true);
    try {
      const { data } = await api.post("/auth/session", { session_id: sessionId });
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      setCompleting(false);
      await finishAuthentication(data);
    } catch (error) {
      setResumeError(errorText(error)); setCompleting(false); setOpen(false);
      callbackMode.current = false;
      toast.error("Google sign-in could not be completed. Please try again.");
      navigate("/", { replace: true, state: { section: section.current, loginError: "Google sign-in could not be completed. Please try again." } });
    }
  }, [finishAuthentication, navigate]);
  const logout = async () => {
    await api.post("/auth/logout");
    acceptUser(null); setLoginAccount(null); setAccountEpoch((value) => value + 1); setRefreshKey((value) => value + 1);
  };

  return <AuthContext.Provider value={{ user, loading, checkAuth, logout, openLogin, completeOAuth, refreshKey, accountEpoch }}>
    {children}
    <AccountLogin open={open} intent={intent} account={loginAccount} completing={completing} resumeError={resumeError} duplicateResume={duplicateResume} hasEntry={!!waiter.current} onAuthenticated={finishAuthentication} onGoogle={googleLogin} onCancel={cancelLogin} onRetry={() => finishAuthentication(loginAccount || user, duplicateResume)} />
  </AuthContext.Provider>;
};
